import { Column, Row } from '@stenajs-webui/core';
import { FlatButton, stenaPlusCircle } from '@stenajs-webui/elements';
import { RadioButtonWithLabel } from '@stenajs-webui/forms';
import { isEmpty, map } from 'lodash';
import type { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEditable } from '../../../../common/modal/EditableContext';
import { trackEvent } from '../../../../common/tracking/trackerService';
import { getGuid } from '../../../../common/utils/guidUtils';
import { transformYesNoOption } from '../../../../common/utils/optionTransformers';
import { DetailsFieldCell } from '../../../../common-ui/details/DetailsFieldCell';
import type { HazardousGoodsStatus } from '../../../../gql/graphql';
import { useUpdateIntermodalFieldValue } from '../../../../services/intermodal/hooks/UseUpdateIntermodalFieldValue';
import type { GoodsTypesQueryRefProps } from '../../../../services/intermodal/hooks/useGoodsTypes';
import {
  addUpdateIntermodalBookingGood,
  clearUpdateBookingGoods,
  getUpdateIntermodalBookingIsSubmitting,
} from '../../../../services/intermodal/updateIntermodalReducer';
import { pluginsRange, yesNoOptions } from '../../../manage/details/components/sections/common';
import {
  getIntermodalHazardousGoodsApprovalInfo,
  transformNoOfPluginsOption,
} from '../../utils/detailsUtils';
import { UpdateIntermodalDropdownField } from '../fields/UpdateIntermodalDropdownField';
import { UpdateIntermodalTextInputField } from '../fields/UpdateIntermodalTextInputField';
import { UpdateBookingGoodsItem } from '../UpdateBookingGoodsItem';

interface UpdateIntermodalCargoSectionProps extends GoodsTypesQueryRefProps {
  hazardousGoods: HazardousGoodsStatus;
}

export const UpdateIntermodalCargoSection: FC<UpdateIntermodalCargoSectionProps> = ({
  hazardousGoods,
  goodsTypesQueryRef,
}) => {
  const bookingGoods = useUpdateIntermodalFieldValue('bookingGoods');
  const dispatch = useDispatch();
  const editable = useEditable();
  const disabled = useSelector(getUpdateIntermodalBookingIsSubmitting) || !editable;
  const hazardousGoodsApprovalInfo = getIntermodalHazardousGoodsApprovalInfo(hazardousGoods);
  const loaded = !isEmpty(bookingGoods);

  const clearGoods = () => dispatch(clearUpdateBookingGoods());
  const addGood = () => dispatch(addUpdateIntermodalBookingGood({ goodId: getGuid() }));

  return (
    <>
      <DetailsFieldCell
        label={'Hazardous goods'}
        renderField={id => (
          <UpdateIntermodalDropdownField
            id={id}
            trackerCategory={'IntermodalUpdate'}
            transformOption={transformYesNoOption}
            name={'hazardousGoods'}
            options={yesNoOptions}
          />
        )}
        approved={hazardousGoodsApprovalInfo ?? undefined}
        flex={1}
      />
      <Row gap={2}>
        <DetailsFieldCell
          label={'Plugin'}
          renderField={id => (
            <UpdateIntermodalDropdownField
              id={id}
              trackerCategory={'IntermodalUpdate'}
              transformOption={transformNoOfPluginsOption}
              options={pluginsRange}
              name={'noOfPlugins'}
            />
          )}
          flex={1}
        />
        <DetailsFieldCell
          label={'Temp (°C)'}
          renderField={id => (
            <UpdateIntermodalTextInputField
              id={id}
              trackerCategory={'IntermodalUpdate'}
              name={'temperature'}
            />
          )}
          flex={1}
        />
      </Row>
      <Column gap>
        <RadioButtonWithLabel
          name={'empty'}
          label={'Empty'}
          disabled={disabled}
          checked={!loaded}
          onClick={loaded ? clearGoods : undefined}
        />
        <RadioButtonWithLabel
          name={'loaded'}
          label={'Loaded'}
          disabled={disabled}
          onClick={!loaded ? addGood : undefined}
          checked={loaded}
        />
      </Column>
      {loaded && (
        <>
          <Column gap={3}>
            {map(bookingGoods, good => (
              <UpdateBookingGoodsItem
                key={good.id}
                value={good}
                editable={editable}
                goodsTypesQueryRef={goodsTypesQueryRef}
              />
            ))}
          </Column>
          <FlatButton
            disabled={disabled}
            leftIcon={stenaPlusCircle}
            label="Add goods"
            onClick={() => {
              trackEvent({ category: 'IntermodalUpdate', action: 'Add goods' });
              dispatch(addUpdateIntermodalBookingGood({ goodId: getGuid() }));
            }}
          />
        </>
      )}
    </>
  );
};
